// components/keyboard/KeyboardShortcutsHelp.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from '@/components/common/Modal';
import { useLocation } from 'react-router-dom';
import { Search, Keyboard, Command } from 'lucide-react';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutItem {
  action: string;
  windows: string;
  macos: string;
  description: string;
}

interface ShortcutCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  shortcuts: ShortcutItem[];
  paths?: string[]; // Paths where this category is relevant
}

export const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('navigation');
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  // Define all shortcut categories
  const categories: ShortcutCategory[] = useMemo(() => [
    {
      id: 'navigation',
      name: 'Navigation',
      icon: <Command size={16} />,
      description: 'Navigate between different sections',
      paths: ['*'], // Always available
      shortcuts: [
        {
          action: 'My Tasks',
          windows: 'Ctrl + Alt + 1',
          macos: 'Cmd + Alt + 1',
          description: 'Navigate to My Tasks page',
        },
        {
          action: 'My Team Tasks',
          windows: 'Ctrl + Alt + 2',
          macos: 'Cmd + Alt + 2',
          description: 'Navigate to My Team Tasks page',
        },
        {
          action: 'All Tasks',
          windows: 'Ctrl + Alt + 3',
          macos: 'Cmd + Alt + 3',
          description: 'Navigate to All Tasks page',
        },
        {
          action: 'Quotations & Invoices',
          windows: 'Ctrl + Alt + 4',
          macos: 'Cmd + Alt + 4',
          description: 'Navigate to Quotations & Invoices page',
        },
        {
          action: 'Reports',
          windows: 'Ctrl + Alt + 5',
          macos: 'Cmd + Alt + 5',
          description: 'Navigate to Reports page',
        },
        {
          action: 'Configurations',
          windows: 'Ctrl + Alt + 6',
          macos: 'Cmd + Alt + 6',
          description: 'Navigate to Configurations page',
        },
      ],
    },
    {
      id: 'tasks',
      name: 'Tasks',
      icon: <Keyboard size={16} />,
      description: 'Task management shortcuts',
      paths: ['/tasks', '/team-tasks', '/all-tasks'],
      shortcuts: [
        {
          action: 'Add New Task',
          windows: 'Ctrl + Alt + N',
          macos: 'Cmd + Option + N',
          description: 'Add new task row to the table',
        },
        {
          action: 'Refresh Tasks',
          windows: 'Ctrl + Alt + R',
          macos: 'Cmd + Alt + R',
          description: 'Refresh the task list',
        },
        {
          action: 'Select All Tasks',
          windows: 'Ctrl + Alt + A',
          macos: 'Cmd + Alt + A',
          description: 'Select or deselect all tasks',
        },
        {
          action: 'Delete Selected',
          windows: 'Delete',
          macos: 'Delete',
          description: 'Delete selected tasks',
        },
        {
          action: 'Export Tasks',
          windows: 'Ctrl + Alt + Shift + E',
          macos: 'Cmd + Alt + Shift + E',
          description: 'Export tasks to CSV',
        },
        {
          action: 'Import Tasks',
          windows: 'Ctrl + Alt + Shift + I',
          macos: 'Cmd + Alt + Shift + I',
          description: 'Import tasks from CSV',
        },
        {
          action: 'Save Changes',
          windows: 'Ctrl + Alt + S',
          macos: 'Cmd + Alt + S',
          description: 'Save all pending changes',
        },
        {
          action: 'End Day',
          windows: 'Ctrl + Shift + D',
          macos: 'Cmd + Shift + D',
          description: 'Open end day modal',
        },
        {
          action: 'Add Client',
          windows: 'Ctrl + Alt + Shift + C',
          macos: 'Cmd + Alt + Shift + C',
          description: 'Open add client modal',
        },
        {
          action: 'Add Status',
          windows: 'Ctrl + Alt + Shift + S',
          macos: 'Cmd + Alt + Shift + S',
          description: 'Open add status modal',
        },
        {
          action: 'Switch to Tab 1',
          windows: 'Ctrl + 1',
          macos: 'Cmd + 1',
          description: 'Switch to first task tab',
        },
        {
          action: 'Switch to Tab 2',
          windows: 'Ctrl + 2',
          macos: 'Cmd + 2',
          description: 'Switch to second task tab',
        },
        {
          action: 'Switch to Tab 3',
          windows: 'Ctrl + 3',
          macos: 'Cmd + 3',
          description: 'Switch to third task tab',
        },
        {
          action: 'Switch to Tab 4',
          windows: 'Ctrl + 4',
          macos: 'Cmd + 4',
          description: 'Switch to fourth task tab',
        },
      ],
    },
    {
      id: 'configuration',
      name: 'Configuration',
      icon: <Keyboard size={16} />,
      description: 'Manage application settings',
      paths: ['/configurations'],
      shortcuts: [
        {
          action: 'Clients',
          windows: 'Ctrl + Alt + C',
          macos: 'Cmd + Alt + C',
          description: 'Go to Clients configuration',
        },
        {
          action: 'Tasks',
          windows: 'Ctrl + Alt + T',
          macos: 'Cmd + Alt + T',
          description: 'Go to Tasks configuration',
        },
        {
          action: 'Sub-tasks',
          windows: 'Ctrl + Alt + S',
          macos: 'Cmd + Alt + S',
          description: 'Go to Sub-tasks configuration',
        },
        {
          action: 'Invoices',
          windows: 'Ctrl + Alt + I',
          macos: 'Cmd + Alt + I',
          description: 'Go to Invoices configuration',
        },
        {
          action: 'Priority',
          windows: 'Ctrl + Alt + P',
          macos: 'Cmd + Alt + P',
          description: 'Go to Priority configuration',
        },
        {
          action: 'Status',
          windows: 'Ctrl + Alt + U',
          macos: 'Cmd + Alt + U',
          description: 'Go to Status configuration',
        },
        {
          action: 'Users',
          windows: 'Ctrl + Alt + R',
          macos: 'Cmd + Alt + R',
          description: 'Go to Users configuration',
        },
        {
          action: 'Preferences',
          windows: 'Ctrl + Alt + E',
          macos: 'Cmd + Alt + E',
          description: 'Go to Preferences configuration',
        },
      ],
    },
    {
      id: 'quotations',
      name: 'Quotations',
      icon: <Keyboard size={16} />,
      description: 'Handle invoices and quotations',
      paths: ['/invoicing'],
      shortcuts: [
        {
          action: 'Pending Invoices',
          windows: 'Ctrl + Alt + P',
          macos: 'Cmd + Option + P',
          description: 'Go to Pending Invoices',
        },
        {
          action: 'Invoices Generated',
          windows: 'Ctrl + Alt + G',
          macos: 'Cmd + Option + G',
          description: 'Go to Invoices Generated',
        },
        {
          action: 'Invoices Sent',
          windows: 'Ctrl + Alt + S',
          macos: 'Cmd + Option + S',
          description: 'Go to Invoices Sent',
        },
      ],
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: <Keyboard size={16} />,
      description: 'Generate and view reports',
      paths: ['/reports'],
      shortcuts: [
        {
          action: 'Day End Report',
          windows: 'Ctrl + Shift + D',
          macos: 'Cmd + Shift + D',
          description: 'Go to Day End Report',
        },
        {
          action: 'Clients Report',
          windows: 'Ctrl + Alt + C',
          macos: 'Cmd + Option + C',
          description: 'Go to Clients Report',
        },
        {
          action: 'Users Report',
          windows: 'Ctrl + Alt + U',
          macos: 'Cmd + Option + U',
          description: 'Go to Users Report',
        },
        {
          action: 'Tasks Report',
          windows: 'Ctrl + Alt + T',
          macos: 'Cmd + Option + T',
          description: 'Go to Tasks Report',
        },
        {
          action: 'Sub-tasks Report',
          windows: 'Ctrl + Alt + S',
          macos: 'Cmd + Option + S',
          description: 'Go to Sub-tasks Report',
        },
      ],
    },
    {
      id: 'general',
      name: 'General',
      icon: <Keyboard size={16} />,
      description: 'General application shortcuts',
      paths: ['*'],
      shortcuts: [
        {
          action: 'Show Help',
          windows: '?',
          macos: '?',
          description: 'Show keyboard shortcuts help',
        },
        {
          action: 'Close Modal',
          windows: 'Escape',
          macos: 'Escape',
          description: 'Close currently open modal',
        },
        {
          action: 'Reset to Default',
          windows: 'Ctrl + Alt + Shift + R',
          macos: 'Cmd + Alt + Shift + R',
          description: 'Reset to default settings',
        },
      ],
    },
  ], []);

  // Auto-detect active tab based on current location
  useEffect(() => {
    if (!isOpen) return;

    const currentPath = location.pathname;
    
    // Find matching category based on current path
    for (const category of categories) {
      if (category.paths?.includes('*')) continue; // Skip global categories
      
      const matches = category.paths?.some(path => 
        currentPath.startsWith(path)
      );
      
      if (matches) {
        setActiveTab(category.id);
        return;
      }
    }
    
    // Default to navigation if no match
    setActiveTab('navigation');
  }, [isOpen, location.pathname, categories]);

  // Filter shortcuts based on search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categories;

    return categories.map(category => ({
      ...category,
      shortcuts: category.shortcuts.filter(shortcut =>
        shortcut.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shortcut.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shortcut.windows.toLowerCase().includes(searchTerm.toLowerCase()) ||
        shortcut.macos.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    })).filter(category => category.shortcuts.length > 0);
  }, [categories, searchTerm]);

  const activeCategory = useMemo(() => {
    return filteredCategories.find(cat => cat.id === activeTab) || filteredCategories[0];
  }, [filteredCategories, activeTab]);

  // Format keyboard shortcut keys
  const formatKeys = (keys: string) => {
    return keys.split(' + ').map((key, index) => (
      <React.Fragment key={index}>
        <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-300 rounded shadow-sm">
          {key}
        </kbd>
        {index < keys.split(' + ').length - 1 && (
          <span className="mx-1 text-gray-400">+</span>
        )}
      </React.Fragment>
    ));
  };

  // Get total shortcuts count
  const totalShortcuts = useMemo(() => {
    return categories.reduce((total, cat) => total + cat.shortcuts.length, 0);
  }, [categories]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Keyboard Shortcuts"
      size="xl"
      showCloseButton={true}
      className='px-3'
    >
      <div className="flex flex-col h-[70vh]">
        {/* Header with Search */}
        <div className="flex-shrink-0 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Keyboard size={16} />
              <span>{totalShortcuts} shortcuts available</span>
              <span className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded">
                {isMac ? 'macOS' : 'Windows'}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              Press <kbd className="px-1 py-0.5 bg-gray-100 border rounded">Esc</kbd> to close
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search shortcuts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-2 py-3 overflow-x-auto border-b border-gray-200">
          {filteredCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveTab(category.id)}
              className={`flex items-center gap-1 px-2 py-1 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                activeTab === category.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-transparent'
              }`}
            >
              {category.icon}
              <span>{category.name}</span>
              <span className="px-1 py-0.5 text-xs bg-white rounded-full">
                {category.shortcuts.length}
              </span>
            </button>
          ))}
        </div>

        {/* Shortcuts Content */}
        <div className="flex-1 overflow-y-auto py-2">
          {activeCategory && (
            <div className="space-y-2">
              <div>
                <h3 className="text-md font-semibold text-gray-900 mb-1">
                  {activeCategory.name}
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  {activeCategory.description}
                </p>
              </div>

              <div className="space-y-1">
                {activeCategory.shortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between px-2 py-1 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-gray-900">
                        {shortcut.action} &nbsp;(  {shortcut.description})
                      </div>
                      {/* <div className="text-sm text-gray-600 mt-0.5">
                        {shortcut.description}
                      </div> */}
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      {formatKeys(isMac ? shortcut.macos : shortcut.windows)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filteredCategories.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No shortcuts found matching "{searchTerm}"
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">💡 Tips</h4>
              <ul className="space-y-1 text-gray-600 text-xs">
                <li>• Shortcuts work when you're not typing in input fields</li>
                <li>• Some shortcuts are context-aware (page-specific)</li>
                <li>• Use {isMac ? 'Option' : 'Alt'} key for page-specific actions</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">🎯 Quick Access</h4>
              <ul className="space-y-1 text-gray-600 text-xs">
                <li>• {isMac ? 'Cmd' : 'Ctrl'} + Alt + 1-6: Main navigation</li>
                <li>• {isMac ? 'Cmd' : 'Ctrl'} + Alt + N: Add new item</li>
                <li>• ?: Show this help anytime</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};